import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Variant } from '@nucleus-commerce/api-client/admin';

import { useProductStock } from '@/features/inventory';
import { StockFigure, type VariantColumn } from '@/features/products';

import {
  AvailableCell,
  SellSummary,
  StorePriceCell,
  StorePriceRange,
  StoreSkuCell,
} from '../components/store-variant-cells';

/** The rows of a store's Variants card: this store's price, SKU, stock and whether it sells. */
export function useStoreVariantColumns(
  storeId: string,
  productId: string,
  currency: string,
): VariantColumn[] {
  const { t } = useTranslation('storeCatalog');
  const { can } = usePermissions();
  const readable = can('inventory.read');
  const stock = useProductStock(productId, readable);
  const byVariant = useMemo(
    () =>
      new Map(
        (stock.data ?? [])
          .filter((row) => row.storeId === storeId)
          .map((row) => [row.variantId, row.available]),
      ),
    [stock.data, storeId],
  );
  const pending = stock.isPending;

  return useMemo(() => {
    const stockOf = (variant: Variant): number | null | 'loading' => {
      if (!readable || !variant.trackInventory) return null;
      return pending ? 'loading' : (byVariant.get(variant.id) ?? 0);
    };
    return [
      {
        id: 'price',
        header: t('storeVariants.columns.price', { currency }),
        width: '7.5rem',
        align: 'end',
        cell: (variant) => <StorePriceCell variant={variant} field="price" className="w-full" />,
        summary: (rows) => <StorePriceRange variants={rows} />,
      },
      {
        id: 'sku',
        header: t('storeVariants.columns.sku'),
        width: '10rem',
        cell: (variant) => <StoreSkuCell variant={variant} className="w-full" />,
      },
      {
        id: 'stock',
        header: t('storeVariants.columns.stock'),
        width: '3.5rem',
        align: 'end',
        cell: (variant) => <StockFigure value={stockOf(variant)} />,
        summary: (rows) => {
          const counted = rows.map(stockOf).filter((value) => typeof value === 'number');
          return counted.length > 0 ? counted.reduce((sum, value) => sum + value, 0) : '—';
        },
      },
      {
        id: 'sell',
        header: t('storeVariants.columns.sell'),
        width: '3.75rem',
        align: 'center',
        cell: (variant) => <AvailableCell variant={variant} />,
        summary: (rows) => <SellSummary variants={rows} />,
      },
    ];
  }, [t, currency, readable, pending, byVariant]);
}
